<template>
  <div id="search-form-visit-options">
    <div class="search-form-visit-options__field">
      <b-form-group label="When would you like to visit?" v-slot="{ ariaDescribedby }">
        <b-form-radio-group
          id="btn-radios-visit"
          v-model="visitOptionModel"
          :options="visitOptions"
          :aria-describedby="ariaDescribedby"
          name="radios-btn-visit"
          buttons
        />
      </b-form-group>
      <div v-if="isCustomDateRange" class="search-form-visit-options__calendar">
        <div class="search-form-visit-options__calendar-wrapper">
          <vc-date-picker
            v-model="selectedDates"
            :value="null"
            color="yellow"
            is-range
            is-expanded
            :columns="2"
            :rows="1"
            :min-date="minDate"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchFormVisitOptions',

  props: {
    visitOption: {
      type: String,
      required: true,
    },
    visitOptions: {
      type: Array,
      required: true,
    },
    dateOpenFrom: {
      type: String,
      default: null,
    },
    dateOpenTo: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      selectedDates: {
        start: null,
        end: null,
      },
      minDate: new Date(),
    }
  },

  computed: {
    visitOptionModel: {
      get() {
        return this.visitOption
      },
      set(value) {
        this.$emit('update:visit-option', value)
      },
    },

    isCustomDateRange() {
      return this.visitOption === 'custom-date-range'
    },
  },

  watch: {
    visitOption: {
      handler(value) {
        if (value !== 'custom-date-range') {
          this.selectedDates = { start: null, end: null }
          this.$emit('update:date-range', { date_open_from: null, date_open_to: null })
        }
      },
    },

    selectedDates: {
      deep: true,
      handler(range) {
        if (range?.start && range?.end) {
          this.$emit('update:date-range', this.formatDateRange(range))
        }
      },
    },

    dateOpenFrom: {
      handler(val) {
        if (val && this.isCustomDateRange) {
          this.selectedDates.start = this.parseDate(val)
        }
      },
    },

    dateOpenTo: {
      handler(val) {
        if (val && this.isCustomDateRange) {
          this.selectedDates.end = this.parseDate(val)
        }
      },
    },
  },

  created() {
    if (this.dateOpenFrom) {
      this.selectedDates.start = this.parseDate(this.dateOpenFrom)
    }
    if (this.dateOpenTo) {
      this.selectedDates.end = this.parseDate(this.dateOpenTo)
    }
  },

  methods: {
    formatDateRange(range) {
      const tzoffset = (new Date()).getTimezoneOffset() * 60000
      const dateOpenFrom = range.start
        ? new Date(range.start.getTime() - tzoffset).toISOString().slice(0, 10)
        : null
      const dateOpenTo = range.end
        ? new Date(range.end.getTime() - tzoffset).toISOString().slice(0, 10)
        : null
      return { date_open_from: dateOpenFrom, date_open_to: dateOpenTo }
    },

    parseDate(isoString) {
      if (!isoString) return null
      return new Date(isoString.replace(/-/g, '/'))
    },
  },
}
</script>

<style scoped lang="scss">

#search-form-visit-options {
  padding: 16px;

  .search-form-visit-options__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  ::v-deep .form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 0;
  }

  ::v-deep .form-group label,
  ::v-deep .form-group legend {
    font-family: "Gill Sans", "Inter", sans-serif;
    font-weight: 600;
    font-size: 18px;
    color: $color-text-dark;
    margin-bottom: 0;
  }

  ::v-deep .btn-group {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    width: 100%;

    @include media-breakpoint-up(lg) {
      grid-template-columns: 1fr 1fr;
    }
  }

  ::v-deep .btn-group .btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    padding: 8px 12px;
    border-radius: 4px;
    border: 1px solid $color-grey-dark-20;
    background-color: #fff;
    font-family: "Gill Sans", "Inter", sans-serif;
    font-size: 18px;
    line-height: 1;
    color: $color-text-dark;

    &:hover:not(.active) {
      border-color: $color-grey-dark-20;
      background-color: rgba($color-yellow-main, 0.15);
    }

    &.active {
      background-color: rgba($color-yellow-main, 0.5);
      border-color: $color-grey-dark;
      font-weight: 600;
    }
  }

  .search-form-visit-options__calendar {
    margin-top: 4px;
    overflow: hidden;
  }

  .search-form-visit-options__calendar-wrapper {
    display: flex;
    flex-wrap: wrap;
    border-radius: 4px;
    background-color: #fff;
    border: 1px solid $color-yellow-light;
    padding: 8px;
  }

  // VCalendar range styling (aligned with FilterButtonDaysOpen)
  ::v-deep .vc-container {
    border: none;

    .vc-day .vc-highlights.vc-day-layer .vc-day-layer {
      .vc-day-box-right-center .vc-highlight.vc-highlight-base-start {
        background-color: $color-yellow-lighter !important;
      }

      .vc-day-box-center-center {
        .vc-highlight {
          background-color: $primary !important;
        }
        .vc-highlight.vc-highlight-base-middle {
          background-color: $color-yellow-lighter !important;
        }
      }
    }

    .vc-day-content.vc-focusable:not(.is-disabled) {
      color: rgb(58, 58, 58) !important;
    }
  }
}
</style>
